import React, { useMemo } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { BaseStyleEnums } from '@nhl/nhl-breakaway-ui';
import { useSize } from '@nhl/react-hooks/lib/useSize';

import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';

import { playersAndTeamsActionCreators } from '../../../../../redux/ducks/playersAndTeams';
import { filterActionCreators } from '../../../../../redux/ducks/playersAndTeams/filter';
import { reportActionCreators } from '../../../../../redux/ducks/playersAndTeams/report';
import { commonMessages } from '../../../../../utility/commonMessages';
import ModuleContainer from '../../../../components/moduleContainer';

import { AdContainer, StatsNavi, StyledTabBar } from './styles';

const HOME_TAB = 'home';
const SKATERS_TAB = 'skaters';
const GOALTENDERS_TAB = 'goalies';
const TEAMS_TAB = 'teams';
const GLOSSARY_TAB = 'glossary';

const StatsMenu = () => {
    const location = useLocation().pathname.replace('/', '');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const screenSize = useSize();

    const isLargeScreen = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;

    const categories = [
        {
            key: HOME_TAB,
            label: <FormattedMessage {...commonMessages.labels.homeMenu} />,
        },
        {
            key: SKATERS_TAB,
            label: <FormattedMessage {...commonMessages.labels.skater} />,
        },
        {
            key: GOALTENDERS_TAB,
            label: <FormattedMessage {...commonMessages.labels.goalie} />,
        },
        {
            key: TEAMS_TAB,
            label: <FormattedMessage {...commonMessages.labels.teams} />,
        },
        {
            key: GLOSSARY_TAB,
            label: <FormattedMessage {...commonMessages.labels.glossary} />,
        },
    ];

    const tabs = useMemo(
        () =>
            categories.map((category) => ({
                key: category.key,
                label: category.label,
                ariaControlId: `${category.key === HOME_TAB ? HOME_TAB : category.key}-tabpanel`,
                link: `/${category.key === HOME_TAB ? '' : category.key}`,
            })),
        [],
    );

    const resetReportSelections = () => {
        dispatch(filterActionCreators.resetAllFilters());
        // When time frame is reset, we also "resetReport" which may, possibly re-run the report.
        dispatch(reportActionCreators.setTimeFrame('season'));
        dispatch(reportActionCreators.resetReportSection('reportName', 'summary'));
        dispatch(reportActionCreators.runReport());
    };

    // When the report category is re-selected or changed we reset all inputs and filters
    const onSelectTab = (tab) => {
        const isDataReport = [SKATERS_TAB, GOALTENDERS_TAB, TEAMS_TAB].includes(tab.key);
        if (tab.key === location && isDataReport) {
            resetReportSelections();
        } else {
            navigate(tab.link);
            if (isDataReport) {
                // Since the location is changed, useEffect (In Report.tsx) will trigger a data re-fetch
                dispatch(playersAndTeamsActionCreators.resetPlayersAndTeamsState(tab.link));
                dispatch(filterActionCreators.resetAllFilters());
            } else {
                dispatch(playersAndTeamsActionCreators.resetPlayersAndTeamsState(categories[1].key));
                dispatch(filterActionCreators.resetAllFilters());
            }
        }
    };

    return (
        <ModuleContainer as="nav">
            <StatsNavi>
                <StyledTabBar
                    tabs={tabs}
                    selectedTab={tabs.find((tab) => tab.key === location) || tabs[0]}
                    onChangeSelectedTab={onSelectTab}
                    className="stats-navigation"
                    isScrollable
                />
                {isLargeScreen && (
                    <AdContainer>
                        <AdTag divId={window.__SAP_DESKTOP_AD_ID__} adType={AdType.SAP_DESKTOP} forceReload />
                    </AdContainer>
                )}
            </StatsNavi>
        </ModuleContainer>
    );
};

export default React.memo(StatsMenu);
